"use client";

import { useCallback, useEffect, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

type FilterTypes = {
	body: undefined | string;
	make: undefined | string;
	type: undefined | string;
	search: undefined | string;
	facility: undefined | string;
};

const Filters = ({
	count,
	onSubmit,
	bodyTypes,
	make,
	vehicleTypes,
	facilities,
	searchParams,
}: {
	count?: number;
	onSubmit: (f: any) => void;
	bodyTypes?: string[];
	make?: string[];
	vehicleTypes?: string[];
	facilities?: { slug: string; name: string }[];
	searchParams?: any;
}) => {
	const sp = useSearchParams();
	const { replace } = useRouter();
	const pathname = usePathname();

	const defaultFilters: FilterTypes = {
		body: searchParams?.body ?? undefined,
		search: searchParams?.search ?? "",
		make: searchParams?.make ?? undefined,
		type: searchParams?.type ?? undefined,
		facility: searchParams?.facility ?? undefined,
	};

	const [filters, setFilters] = useState<FilterTypes>(defaultFilters);
	const [search, setSearch] = useState(filters?.search ?? "");

	useEffect(() => {
		const params = new URLSearchParams(searchParams);
		const p: any = Object.fromEntries(params);
		if (p?.search) setSearch(p.search);
		setFilters(p);
		onSubmit(p);
	}, []);

	// Debounced search
	useEffect(() => {
		const timer = setTimeout(() => {
			if (search !== filters.search) {
				const updated = { ...filters, search };
				setFilters(updated);
				handleFilter(updated);
			}
		}, 350);
		return () => clearTimeout(timer);
	}, [search]);

	const handleFilter = useCallback(
		(f: FilterTypes) => {
			const params = new URLSearchParams(sp);

			// Preserve take param
			const currentTake = params.get("take");

			// Reset take when filters change (user wants to see from start)
			params.delete("take");

			if (f?.search) params.set("search", f.search);
			else params.delete("search");

			if (f?.make) params.set("make", f.make);
			else params.delete("make");

			if (f?.body) params.set("body", f.body);
			else params.delete("body");

			if (f?.type) params.set("type", f.type);
			else params.delete("type");

			if (f?.facility) params.set("facility", f.facility);
			else params.delete("facility");

			const qs = params.toString();
			replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
			onSubmit(f);
		},
		[sp, replace, pathname, onSubmit]
	);

	const hasActiveFilters = !!filters.body || !!filters.make || !!filters.type || !!filters.search || !!filters.facility;

	const clearAll = () => {
		const cleared: FilterTypes = {
			body: undefined,
			make: undefined,
			type: undefined,
			search: undefined,
			facility: undefined,
		};
		setSearch("");
		setFilters(cleared);
		handleFilter(cleared);
	};

	return (
		<div className="mb-10 -mt-14 relative z-20">
			<div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-5 lg:p-8 space-y-4 backdrop-blur-sm">
				{/* Search bar */}
				<div className="relative">
					<FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
					<input
						onChange={(e) => setSearch(e.target.value)}
						className="w-full pl-10 pr-10 px-4 py-3 rounded-lg text-slate-200 placeholder:text-slate-500 focus:outline-none transition-colors bg-surface border border-white/[0.06] focus:border-accent/50"
						value={search}
						placeholder="Sök märke, modell..."
					/>
					{search && (
						<button
							onClick={() => setSearch("")}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
						>
							<FaTimes size={12} />
						</button>
					)}
				</div>

				{/* Filter row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
					<select
						value={filters.type ?? ""}
						onChange={(e) => {
							// Reset body type when switching vehicle type so a stale
							// MC/car body filter doesn't linger and zero out results.
							const updated = { ...filters, type: e.target.value || undefined, body: undefined };
							setFilters(updated);
							handleFilter(updated);
						}}
						className="w-full px-4 py-3 rounded-lg text-slate-200 focus:outline-none transition-colors appearance-none bg-surface border border-white/[0.06] focus:border-accent/50"
					>
						<option value="">Alla typer</option>
						{vehicleTypes?.map((bt) => (
							<option key={bt} value={bt}>
								{vehicleTypeContent(bt)}
							</option>
						))}
					</select>

					<select
						value={filters.body ?? ""}
						onChange={(e) => {
							const updated = { ...filters, body: e.target.value || undefined };
							setFilters(updated);
							handleFilter(updated);
						}}
						className="w-full px-4 py-3 rounded-lg text-slate-200 focus:outline-none transition-colors appearance-none bg-surface border border-white/[0.06] focus:border-accent/50"
					>
						<option value="">Alla fordonstyper</option>
						{bodyTypes?.map((bt) => (
							<option key={bt} value={bt}>
								{bt}
							</option>
						))}
					</select>

					<select
						value={filters.make ?? ""}
						onChange={(e) => {
							const updated = { ...filters, make: e.target.value || undefined };
							setFilters(updated);
							handleFilter(updated);
						}}
						className="w-full px-4 py-3 rounded-lg text-slate-200 focus:outline-none transition-colors appearance-none bg-surface border border-white/[0.06] focus:border-accent/50"
					>
						<option value="">Alla märken</option>
						{make?.map((m) => (
							<option key={m} value={m}>
								{m}
							</option>
						))}
					</select>

					<select
						value={filters.facility ?? ""}
						onChange={(e) => {
							const updated = { ...filters, facility: e.target.value || undefined };
							setFilters(updated);
							handleFilter(updated);
						}}
						className="w-full px-4 py-3 rounded-lg text-slate-200 focus:outline-none transition-colors appearance-none bg-surface border border-white/[0.06] focus:border-accent/50"
					>
						<option value="">Alla anläggningar</option>
						{facilities?.map((f) => (
							<option key={f.slug} value={f.slug}>
								{f.name}
							</option>
						))}
					</select>
				</div>

				{/* Active filters + count row */}
				{(hasActiveFilters || typeof count === "number") && (
					<div className="flex items-center justify-between pt-1">
						<div className="flex items-center gap-2 flex-wrap">
							{typeof count === "number" && <span className="text-sm text-gray-400">{count} fordon</span>}
						</div>
						{hasActiveFilters && (
							<button
								onClick={clearAll}
								className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1.5"
							>
								<FaTimes size={10} />
								Rensa filter
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

const vehicleTypeContent = (type: string) => {
	switch (type) {
		case "MC":
			return "Motorcyklar";
		case "TransportVehicle":
			return "Transport";
		default:
			return "Bilar";
	}
};

export default Filters;
